import { makeAutoObservable, runInAction } from "mobx";
import {
  superadminDiscountsList,
  superadminDiscountsListUsages,
  superadminDiscountsUpdate,
} from "@pro-sto/api-client";
import type {
  DiscountResponseDto,
  DiscountUsageResponseDto,
  PaginationMetaDto,
} from "@pro-sto/api-client";
import { toastStore } from "./ToastStore";

export type DiscountListFilter = "all" | "active" | "promocode" | "manual";

function parseMeta(
  meta: { [key: string]: unknown } | undefined,
  fallbackLimit: number,
  itemCount: number,
): PaginationMetaDto {
  const total = (meta?.total as number | undefined) ?? itemCount;
  const page = (meta?.page as number | undefined) ?? 1;
  const limit = (meta?.limit as number | undefined) ?? fallbackLimit;
  const totalPages =
    (meta?.totalPages as number | undefined) ??
    (limit > 0 ? Math.ceil(total / limit) : 0);
  return {
    total,
    page,
    limit,
    totalPages,
    hasNextPage:
      (meta?.hasNextPage as boolean | undefined) ?? page < totalPages,
    hasPrevPage: (meta?.hasPrevPage as boolean | undefined) ?? page > 1,
  };
}

class DiscountsStore {
  discounts: DiscountResponseDto[] = [];
  meta: PaginationMetaDto | null = null;
  isLoading = false;
  listFilter: DiscountListFilter = "all";
  page = 1;
  readonly limit = 20;
  togglingUuid: string | null = null;

  usages: DiscountUsageResponseDto[] = [];
  usagesMeta: PaginationMetaDto | null = null;
  usagesDiscountTitle = "";
  isUsagesLoading = false;
  usagesPage = 1;
  readonly usagesLimit = 20;

  constructor() {
    makeAutoObservable(this);
  }

  private listQueryParams() {
    const base: {
      page: number;
      limit: number;
      type?: "manual" | "promocode";
      isActive?: boolean;
    } = {
      page: this.page,
      limit: this.limit,
    };
    switch (this.listFilter) {
      case "active":
        base.isActive = true;
        break;
      case "promocode":
        base.type = "promocode";
        break;
      case "manual":
        base.type = "manual";
        break;
      default:
        break;
    }
    return base;
  }

  loadDiscounts = async () => {
    this.isLoading = true;
    try {
      const response = await superadminDiscountsList(this.listQueryParams());
      runInAction(() => {
        this.discounts = response.data ?? [];
        this.meta = parseMeta(response.meta, this.limit, this.discounts.length);
        this.isLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError("Не удалось загрузить скидки");
    }
  };

  setListFilter = (filter: DiscountListFilter) => {
    if (this.listFilter === filter) return;
    this.listFilter = filter;
    this.page = 1;
    void this.loadDiscounts();
  };

  setPage = (page: number) => {
    this.page = page;
    void this.loadDiscounts();
  };

  toggleActive = async (discount: DiscountResponseDto, isActive: boolean) => {
    this.togglingUuid = discount.uuid;
    try {
      await superadminDiscountsUpdate({
        uuid: discount.uuid,
        requestBody: { is_active: isActive },
      });
      runInAction(() => {
        const idx = this.discounts.findIndex((d) => d.uuid === discount.uuid);
        if (idx >= 0) {
          this.discounts[idx] = { ...this.discounts[idx], is_active: isActive };
        }
        this.togglingUuid = null;
      });
    } catch {
      runInAction(() => {
        this.togglingUuid = null;
      });
      toastStore.showError("Не удалось обновить статус скидки");
    }
  };

  resetUsages = () => {
    this.usages = [];
    this.usagesMeta = null;
    this.usagesDiscountTitle = "";
    this.usagesPage = 1;
  };

  loadUsages = async (discountUuid: string, title?: string) => {
    this.isUsagesLoading = true;
    if (title) this.usagesDiscountTitle = title;
    try {
      const response = await superadminDiscountsListUsages({
        uuid: discountUuid,
        page: this.usagesPage,
        limit: this.usagesLimit,
      });
      runInAction(() => {
        this.usages = response.data ?? [];
        this.usagesMeta = parseMeta(
          response.meta,
          this.usagesLimit,
          this.usages.length,
        );
        this.isUsagesLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isUsagesLoading = false;
      });
      toastStore.showError("Не удалось загрузить применения");
    }
  };

  setUsagesPage = (page: number, discountUuid: string) => {
    this.usagesPage = page;
    void this.loadUsages(discountUuid);
  };
}

export const discountsStore = new DiscountsStore();
export type { DiscountsStore };
